import React, { useEffect, useState } from "react";

import cx from "classnames";
import { scaleLinear } from "d3-scale";
import { select, pointer } from "d3-selection";

import "d3-transition";
import { coalesce } from "@kazoohr/helpers";

import { useChartContext } from "./ChartContext";
import ChartTooltip, { tooltipCoords } from "./ChartTooltip";
import {
  calculatePercents,
  percentsPosition,
  totalData,
  StatusBarItemType,
  PercentData,
  ElementPosition,
} from "./helpers";
import styles from "./StatusBarChart.scss";

export const translateTo = (x = 0, y = 0, xOffSet = 0, yOffSet = 0) =>
  "translate(" + (x + xOffSet) + "," + (y + yOffSet) + ")";

// eslint-disable-next-line max-lines-per-function
export default function StackedBar() {
  const state = useChartContext();
  const [selectedData, setSelectedData] = useState<StatusBarItemType>(
    state?.data[0].tag ?? StatusBarItemType.ON_TRACK
  );
  const [showTooltip, setShowTooltip] = useState(false);

  const {
    svgRef,
    data,
    newBarHeight,
    newTop,
    linkLabel,
    tooltipContent,
    tooltips,
  } = coalesce(state, {});

  // eslint-disable-next-line max-lines-per-function
  useEffect(() => {
    const svgElement = select(svgRef.current);
    const width = parseInt(svgElement.style("width"), 10);

    const config = {
      margin: { top: newTop, right: 8, bottom: 8, left: 8 },
      height: 50,
      barHeight: newBarHeight,
    };
    const { margin, barHeight } = config;

    const yPercentBar = margin.top;
    const spacingPercentLabel = 12;
    const yPercentLabel = barHeight + margin.top + spacingPercentLabel;

    const total = totalData(data);
    const percents = calculatePercents(data, total);
    const xScale = scaleLinear().domain([0, total]).range([0, width]);

    const percentScale = (acc: number, value: number, total: number) => {
      const percent = (value / total) * 100;
      const minPercent = 2;
      const percentWidth = 12;
      const result =
        acc === 0
          ? 0
          : acc + value === total && percent < minPercent
          ? xScale(total) - percentWidth
          : xScale(acc);

      return result;
    };

    const gElement = svgElement.select<SVGGElement>(".chart-bars");

    function mouseEnter(event: MouseEvent, d: PercentData) {
      setSelectedData(d.tag);
      setShowTooltip(true);
    }

    function mouseMove(event: MouseEvent) {
      if (!(event.currentTarget instanceof Element)) {
        return;
      }

      const element = select(event.currentTarget);
      element.transition().duration(50).attr("opacity", ".85");

      const svgElement = select(svgRef.current);
      const [xPoint, yPoint] = tooltipCoords(
        svgRef,
        pointer(event, event.currentTarget)
      );

      svgElement
        .select(".chart-tooltip")
        .attr("transform", translateTo(xPoint, yPoint));
    }

    function mouseOut(event: MouseEvent) {
      if (!(event.currentTarget instanceof Element)) {
        return;
      }

      const element = select(event.currentTarget);
      element.transition().duration(50).attr("opacity", "1");
      setShowTooltip(false);
    }

    const rectSelection = gElement
      .selectAll<SVGRectElement, PercentData>("rect")
      .data(percents)
      .join("rect");

    rectSelection
      .attr("class", (d) => {
        return cx("rect-stacked", {
          [styles["status-indicator-on-track"]]:
            StatusBarItemType.ON_TRACK === d.tag,
          [styles["status-indicator-behind"]]:
            StatusBarItemType.BEHIND === d.tag,
          [styles["status-indicator-at-risk"]]:
            StatusBarItemType.AT_RISK === d.tag,
        });
      })
      .style("cursor", "pointer")
      .attr("x", (d) => percentScale(d.cumulative, d.points, d.total))
      .attr("y", yPercentBar)
      .attr("height", barHeight)
      .attr("width", (d) => percentScale(d.points, d.points, d.total))
      .attr("data-testid", (d) => "chart-bar-" + d.tag)
      .on("mouseenter", function (event: MouseEvent, d: PercentData) {
        mouseEnter(event, d);
      })
      .on("mousemove", function (event: MouseEvent) {
        mouseMove(event);
      })
      .on("mouseout", function (event: MouseEvent) {
        mouseOut(event);
      });

    const percentsNoZero = percents.filter((p) => p.points !== 0);
    const elemPositions = percentsPosition(percentsNoZero, percentScale);

    const textSelection = gElement
      .selectAll<SVGTextElement, ElementPosition>("text")
      .data(elemPositions)
      .join("text");

    textSelection
      .attr("class", styles["percent-label"])
      .attr("x", (d) => d.x1)
      .attr("y", yPercentLabel)
      .text((d) => (Number(d.percent) > 0 ? d.percent + "%" : ""));

    const yLineOffSet = 4;
    const lineSelection = gElement
      .selectAll<SVGLineElement, ElementPosition>("line")
      .data(elemPositions)
      .join("line");

    lineSelection
      .attr("class", (d) => {
        return cx("rect-stacked", {
          [styles["status-indicator-on-track"]]:
            StatusBarItemType.ON_TRACK === d.tag,
          [styles["status-indicator-behind"]]:
            StatusBarItemType.BEHIND === d.tag,
          [styles["status-indicator-at-risk"]]:
            StatusBarItemType.AT_RISK === d.tag,
        });
      })
      .attr("x1", (d) => (Number(d.percent) < 1 ? 0 : d.x1))
      .attr("x2", (d) => (Number(d.percent) < 1 ? 0 : d.x2))
      .attr("y1", yPercentLabel + yLineOffSet)
      .attr("y2", yPercentLabel + yLineOffSet);
  }, [data, linkLabel, newBarHeight]);

  return (
    <>
      <g className="chart-bars" />
      <ChartTooltip
        svgRef={svgRef}
        data={data}
        selectedTag={selectedData}
        showTooltip={showTooltip}
        tooltipContent={tooltipContent}
        tooltips={tooltips}
      />
    </>
  );
}
